import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Eye, Trash2, Upload } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useNotify, Confirm } from 'react-admin';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import ExperimentDataDetail, { ExperimentItem, ExperimentRecord } from './ExperimentDataDetail.tsx';

declare const process: {
  env: {
    REACT_APP_API_URL?: string;
  };
};

type TemplateType = 'AQ' | 'BN';

interface AQRow {
  labSampleId: string;
  yourSampleId: string;
  sampleDescription: string;
  batchNumber: string;
  timeRecord: string | null;
  analysis: string;
  analysisVariation: string;
  sampleResult: string;
  testValue: string;
  unit: string;
  inSpec: string;
}

interface BNParseResult {
  experiment: {
    reference?: string;
    labSampleId: string;
    yourSampleId: string;
    sampleDescription: string;
    batchNumber: string;
    description?: string;
    qcType?: string;
    sampleInformation?: string | null;
    timeRecord: string | null;
  };
  items: Array<{
    labSampleId?: string;
    yourSampleId?: string;
    analysis: string;
    testMethod: string;
    testValue: string;
    unit: string;
    loq?: string;
  }>;
}

interface RawUser {
  id?: number | string;
  Id?: number | string;
  userId?: number | string;
  UserId?: number | string;
  userName?: string;
  username?: string;
  name?: string;
  Name?: string;
}

const normalizeHeader = (value: string) => value
  .replace(/^\uFEFF/, '')
  .trim()
  .toLowerCase()
  .replace(/^"+|"+$/g, '')
  .replace(/[_\s\-/()?:.]+/g, '');
const textOrEmpty = (value: unknown) => String(value ?? '').trim();

const pickMappedText = (entries: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(entries, key)) {
      const value = textOrEmpty(entries[key]);
      if (value) return value;
    }
  }
  return '';
};

const isExcelDateLikeValue = (value: string) => /^(\d{1,2}\/\d{1,2}\/\d{2,4}|\d{4,5})$/.test(value);

const normalizeTrailingZeroDecimal = (value: string) => {
  const trimmed = value.trim();
  const decimalMatch = trimmed.match(/^([<>]=?)?\s*(\d+)\.(\d+)$/);
  if (!decimalMatch) {
    return trimmed;
  }

  const prefix = decimalMatch[1] || '';
  const integerPart = decimalMatch[2];
  const fractionalPart = decimalMatch[3].replace(/0+$/, '');

  if (!fractionalPart) {
    return `${prefix}${integerPart}`;
  }

  return `${prefix}${integerPart}.${fractionalPart}`;
};

const pickAQTestValue = (entries: Record<string, unknown>) => {
  const directValue = normalizeTrailingZeroDecimal(filterExcelDateSerial(pickMappedText(entries, ['value', 'testvalue', '数值', '检测值'])));
  if (directValue && !isExcelDateLikeValue(directValue)) {
    return directValue;
  }

  const fallbackCandidates = Object.values(entries)
    .map((value) => textOrEmpty(value))
    .map((value) => normalizeTrailingZeroDecimal(value))
    .filter((value) => /^(<|>)/.test(value) || /^\d+\.\d+$/.test(value));

  return fallbackCandidates[0] || directValue;
};

const decodeCsvText = (arrayBuffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(arrayBuffer);

  const utf8 = new TextDecoder('utf-8').decode(bytes);
  const utf8ReplacementCount = (utf8.match(/\uFFFD/g) || []).length;
  if (utf8ReplacementCount === 0) {
    return utf8;
  }

  try {
    const gb18030 = new TextDecoder('gb18030').decode(bytes);
    const gbReplacementCount = (gb18030.match(/\uFFFD/g) || []).length;
    return gbReplacementCount <= utf8ReplacementCount ? gb18030 : utf8;
  } catch {
    return utf8;
  }
};

const toIsoTime = (value: unknown): string => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString();
  if (typeof value === 'number') {
    const jsDate = XLSX.SSF.parse_date_code(value);
    if (jsDate) {
      const date = new Date(jsDate.y, jsDate.m - 1, jsDate.d, jsDate.H, jsDate.M, Math.round(jsDate.S));
      if (!Number.isNaN(date.getTime())) return date.toISOString();
    }
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    const date = new Date(trimmed);
    if (!Number.isNaN(date.getTime())) return date.toISOString();
  }
  return '';
};

const filterExcelDateSerial = (value: string): string => {
  if (value.includes('<') || value.includes('>') || value.includes('.')) {
    return value;
  }
  if (/^\d{4,5}$/.test(value)) {
    const num = Number.parseInt(value, 10);
    if (num > 10000 && num < 60000) {
      return '';
    }
  }
  return value;
};

const parseAQRows = (sheet: XLSX.WorkSheet): AQRow[] => {
  const rawRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '', raw: false });

  const mapped = rawRows
    .map((row) => {
      const entries = Object.entries(row).reduce<Record<string, unknown>>((acc, [key, value]) => {
        acc[normalizeHeader(key)] = value;
        return acc;
      }, {});

      return {
        labSampleId: pickMappedText(entries, ['labsampleid', '实验室样品编号', 'labid']),
        yourSampleId: pickMappedText(entries, ['yoursampleid', '样品编号', 'sampleid']),
        sampleDescription: pickMappedText(entries, ['sampledescription', '样品描述', 'description']),
        batchNumber: pickMappedText(entries, ['batchlot', 'batchnumber', '批号']),
        timeRecord: toIsoTime(pickMappedText(entries, ['time', 'timerecord'])) || null,
        analysis: pickMappedText(entries, ['analysis', '检测项目', '项目']),
        analysisVariation: pickMappedText(entries, ['analysisvariation', '检测项目变体', 'variation']),
        sampleResult: pickMappedText(entries, ['result', 'sampleresult', 'testresult', '结果', '检测结果']),
        testValue: pickAQTestValue(entries),
        unit: pickMappedText(entries, ['unit', '单位']),
        inSpec: pickMappedText(entries, ['inspec', 'spec', '判定', '是否符合']),
      } as AQRow;
    })
    .filter((row) => row.labSampleId || row.analysis || row.testValue);

  if (mapped.length === 0) {
    throw new Error('AQ template parse failed: no valid data rows found.');
  }

  return mapped;
};

const parseBNRows = (sheet: XLSX.WorkSheet): BNParseResult[] => {
  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '', raw: false }) as unknown[][];
  if (rows.length < 5) {
    throw new Error('BN template parse failed: template rows are incomplete.');
  }

  const headerRow = rows[0] || [];

  const columnMap = headerRow.reduce<Record<string, number>>((acc, cell, index) => {
    acc[normalizeHeader(String(cell ?? ''))] = index;
    return acc;
  }, {});

  const getColumnIndex = (aliases: string[]) => {
    const normalizedAliases = aliases.map((alias) => normalizeHeader(alias));
    for (const alias of normalizedAliases) {
      if (typeof columnMap[alias] === 'number') {
        return columnMap[alias];
      }
    }

    for (const [key, index] of Object.entries(columnMap)) {
      if (normalizedAliases.some((alias) => key.includes(alias) || alias.includes(key))) {
        return index;
      }
    }

    return -1;
  };

  const referenceIdx = getColumnIndex(['reference']);
  const descriptionIdx = getColumnIndex(['description']);
  const sampleDescIdx = getColumnIndex(['sampedescription', 'sampledescription', 'sampledesc', 'sampedesc']);
  const batchIdx = getColumnIndex(['batchno', 'batchnumber', 'batch']);
  const sampleNoIdx = getColumnIndex(['sampleno', 'sampleid', 'samplenum']);
  const qcTypeIdx = getColumnIndex(['qctype', 'qc']);
  const sampleInfoIdx = getColumnIndex(['sampleinformation', 'sampleinfo']);
  const timeIdx = getColumnIndex(['time', 'timerecord']);

  if ([referenceIdx, sampleDescIdx, batchIdx].some((i) => i < 0)) {
    throw new Error('BN template parse failed: required headers (Reference/Sampe Description/Batch No.) are missing.');
  }

  const analyteStartIndex = (timeIdx >= 0 ? timeIdx + 1 : 8);
  let analyteColumns = headerRow
    .map((value, index) => ({ title: textOrEmpty(value), index }))
    .filter((entry) => entry.index >= analyteStartIndex && !!entry.title);

  // Fallback 1: if no analyte columns found using the assumed start index,
  // consider any non-metadata non-empty headers as analytes.
  if (analyteColumns.length === 0) {
    const metadataKeys = new Set([
      'reference',
      'description',
      'sampledescription',
      'sampledesc',
      'batchno',
      'batchnumber',
      'batch',
      'sampleno',
      'sampleid',
      'samplenum',
      'qctype',
      'qc',
      'sampleinformation',
      'sampleinfo',
      'time',
      'timerecord',
      'iu',
      'units',
      'unit',
      'loq',
      'method',
      'testmethod',
    ]);

    analyteColumns = headerRow
      .map((value, index) => ({ title: textOrEmpty(value), index, normalized: normalizeHeader(String(value ?? '')) }))
      .filter((entry) => entry.title && !metadataKeys.has(entry.normalized));
  }

  // Fallback 2: if still none, scan the value row (row 5 or located value row)
  // for any columns that contain a non-empty test value.
  if (analyteColumns.length === 0) {
    const potentialValueRow = rows[4] || rows.find((r) => (r || []).some((c) => String(c ?? '').trim()));
    if (potentialValueRow) {
      analyteColumns = potentialValueRow
        .map((cell, idx) => ({ value: String(cell ?? '').trim(), index: idx }))
        .filter((c) => c.value)
        .map((c) => ({ title: headerRow[c.index] ? textOrEmpty(headerRow[c.index]) : '', index: c.index }))
        .filter((entry) => entry.title);
    }
  }

  if (analyteColumns.length === 0) {
    throw new Error('BN template parse failed: no analyte columns found.');
  }

  const findRowByLabel = (labels: string[]) => {
    const normalized = labels.map((label) => normalizeHeader(label));
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i] || [];
      const firstCell = normalizeHeader(String(row[0] ?? ''));
      if (normalized.includes(firstCell)) {
        return i;
      }
    }
    return -1;
  };

  const valueRowIndex = rows[4] ? 4 : findRowByLabel(['value', 'testvalue', '结果值']);
  if (valueRowIndex < 0) {
    throw new Error('BN template parse failed: value row not found.');
  }

  const valueRow = rows[valueRowIndex] || [];
  const unitRow = rows[findRowByLabel(['units', 'unit', '单位'])] || rows[1] || [];
  const loqRow = rows[findRowByLabel(['loq'])] || rows[2] || [];
  const methodRow = rows[findRowByLabel(['method', 'testmethod', '方法'])] || rows[3] || [];

  const labSampleId = textOrEmpty(valueRow[referenceIdx]);
  const batchNumber = textOrEmpty(valueRow[batchIdx]);
  if (!labSampleId && !batchNumber) {
    throw new Error('BN template parse failed: no value found in row 5 for Reference/Batch No.');
  }

  const timeRecord = toIsoTime(valueRow[timeIdx]) || null;

  const toAnalysisGroupName = (rawTitle: string) => {
    const title = textOrEmpty(rawTitle);
    if (!title) return '';
    return title.replace(/\s*-\s*\d+\s*$/, '').trim() || title;
  };

  const groupedItemMap = new Map<string, {
    analysis: string;
    testMethod: string;
    unit: string;
    loq: string;
    testValues: string[];
  }>();

  analyteColumns.forEach((column) => {
    let testValue = normalizeTrailingZeroDecimal(filterExcelDateSerial(textOrEmpty(valueRow[column.index])));
    if (!testValue) return;

    const analysis = toAnalysisGroupName(column.title);
    const groupKey = normalizeHeader(analysis || column.title);
    const testMethod = textOrEmpty(methodRow[column.index]);
    const unit = textOrEmpty(unitRow[column.index]) || '-';
    const loq = textOrEmpty(loqRow[column.index]);

    const existing = groupedItemMap.get(groupKey);
    if (!existing) {
      groupedItemMap.set(groupKey, {
        analysis: analysis || column.title,
        testMethod,
        unit,
        loq,
        testValues: [testValue],
      });
      return;
    }

    existing.testValues.push(testValue);
  });

  const itemPayload = Array.from(groupedItemMap.values()).map((group) => ({
    analysis: group.analysis,
    testMethod: group.testMethod,
    unit: group.unit,
    loq: group.loq,
    testValue: group.testValues.join(', '),
  }));

  if (itemPayload.length === 0) {
    throw new Error('BN template parse failed: no valid test values found in row 5.');
  }

  const sampleNo = textOrEmpty(valueRow[sampleNoIdx]);
  const yourSampleId = sampleNo ? `${batchNumber}-${sampleNo}` : batchNumber;

  const payloads: BNParseResult[] = [
    {
      experiment: {
        reference: labSampleId,
        labSampleId,
        yourSampleId,
        sampleDescription: textOrEmpty(valueRow[sampleDescIdx]),
        batchNumber,
        description: textOrEmpty(valueRow[descriptionIdx]),
        qcType: textOrEmpty(valueRow[qcTypeIdx]),
        sampleInformation: textOrEmpty(valueRow[sampleInfoIdx] || valueRow[getColumnIndex(['iu'])]) || null,
        timeRecord,
      },
      items: itemPayload,
    },
  ];

  if (payloads.length === 0) {
    throw new Error('BN template parse failed: no valid experiment rows found.');
  }

  return payloads;
};

export default function ExperimentDataPage() {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
  const notify = useNotify();

  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importTemplateType, setImportTemplateType] = useState<TemplateType>('AQ');
  const [importFile, setImportFile] = useState<File | null>(null);
  const importFileInputRef = useRef<HTMLInputElement | null>(null);
  const [importing, setImporting] = useState(false);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [batchNumberQuery, setBatchNumberQuery] = useState('');
  const [listFilter, setListFilter] = useState<{ batchNumber: string } | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [deletingExperimentId, setDeletingExperimentId] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [overwriteConfirmOpen, setOverwriteConfirmOpen] = useState(false);
  const [overwriteConfirmMessage, setOverwriteConfirmMessage] = useState('');
  const overwriteConfirmResolverRef = useRef<((confirmed: boolean) => void) | null>(null);
  const [experiments, setExperiments] = useState<ExperimentRecord[]>([]);
  const [userNameMap, setUserNameMap] = useState<Record<string, string>>({});

  const [detailExperiment, setDetailExperiment] = useState<ExperimentRecord | null>(null);
  const [detailItems, setDetailItems] = useState<ExperimentItem[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalCount / perPage)), [totalCount, perPage]);

  const buildHeaders = (isJson = false): HeadersInit => {
    const token = localStorage.getItem('token');
    return {
      ...(isJson ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const fetchExperiments = useCallback(async (p: number, pp: number, filterObj: { batchNumber: string } | null) => {
    setLoadingList(true);
    try {
      const start = (p - 1) * pp;
      const end = start + pp - 1;
      const params = new URLSearchParams();
      params.set('range', `[${start},${end}]`);
      params.set('perPage', String(pp));

      const queryFilter: Record<string, string> = {};
      if (filterObj?.batchNumber) {
        queryFilter.batchNumber = filterObj.batchNumber;
      }
      if (Object.keys(queryFilter).length > 0) {
        params.set('filter', JSON.stringify(queryFilter));
      }

      const response = await fetch(`${API_URL}/experiments?${params.toString()}`, {
        method: 'GET',
        headers: buildHeaders(false),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const list = (await response.json()) as ExperimentRecord[];
      const contentRange = response.headers.get('Content-Range') || '';
      const matchedTotal = /\/(\d+)$/.exec(contentRange);
      const total = matchedTotal ? Number.parseInt(matchedTotal[1], 10) : list.length;

      setExperiments(list);
      setTotalCount(total);
      setPage(p);
      setPerPage(pp);
    } catch (error) {
      notify(`Load experiment list failed: ${(error as Error).message}`, { type: 'error' });
    } finally {
      setLoadingList(false);
    }
  }, [API_URL, notify]);

  useEffect(() => {
    fetchExperiments(page, perPage, listFilter);
  }, [fetchExperiments, page, perPage, listFilter]);

  useEffect(() => {
    const debounceTimer = window.setTimeout(() => {
      const keyword = batchNumberQuery.trim();
      setPage(1);
      setListFilter(keyword ? { batchNumber: keyword } : null);
    }, 300);

    return () => window.clearTimeout(debounceTimer);
  }, [batchNumberQuery]);

  const fetchUserNameMap = useCallback(async () => {
    const endpoints = [
      `${API_URL}/permissionUsers/all`,
      `${API_URL}/permissionUsers?range=[0,9999]&perPage=10000`,
    ];

    for (const endpoint of endpoints) {
      try {
        const response = await fetch(endpoint, {
          method: 'GET',
          headers: buildHeaders(false),
        });

        if (!response.ok) {
          continue;
        }

        const payload = await response.json();
        const users: RawUser[] = Array.isArray(payload)
          ? payload
          : (Array.isArray(payload?.data) ? payload.data : []);

        if (!users.length) {
          continue;
        }

        const map: Record<string, string> = {};
        users.forEach((user) => {
          const rawId = user?.id ?? user?.Id ?? user?.userId ?? user?.UserId;
          const rawName = user?.userName ?? user?.username ?? user?.name ?? user?.Name;
          if (rawId !== undefined && rawId !== null && rawName) {
            map[String(rawId)] = String(rawName);
          }
        });

        setUserNameMap(map);
        return;
      } catch {
        // Try the next endpoint.
      }
    }
  }, [API_URL]);

  useEffect(() => {
    fetchUserNameMap();
  }, [fetchUserNameMap]);

  const uploadExperiment = async (
    experimentPayload: {
      reference?: string;
      sampleDescription: string;
      batchNumber: string;
      description?: string;
      qcType?: string;
      sampleInformation?: string | null;
      timeRecord: string | null;
      exResult: number;
      templateType: TemplateType;
    },
    itemPayloads: Array<{
      labSampleId?: string;
      yourSampleId?: string;
      analysis?: string;
      analysisVariation?: string;
      testMethod?: string;
      unit: string;
      sampleResult?: string;
      testValue: string;
      loq?: string;
      inSpec?: string;
      testResults?: string;
    }>
  ) => {
    const createExperimentResponse = await fetch(`${API_URL}/experiments`, {
      method: 'POST',
      headers: buildHeaders(true),
      body: JSON.stringify({
        Reference: experimentPayload.reference || null,
        SampleDescription: experimentPayload.sampleDescription,
        BatchNumber: experimentPayload.batchNumber,
        Description: experimentPayload.description || null,
        QCType: experimentPayload.qcType || null,
        SampleInformation: experimentPayload.sampleInformation || null,
        TemplateType: experimentPayload.templateType,
        TimeRecord: experimentPayload.timeRecord || null,
        ExResult: experimentPayload.exResult,
      }),
    });

    if (!createExperimentResponse.ok) {
      const text = await createExperimentResponse.text();
      throw new Error(text || `Create experiment failed: HTTP ${createExperimentResponse.status}`);
    }

    const createdExperiment = (await createExperimentResponse.json()) as ExperimentRecord;
    const experimentId = Number.parseInt(createdExperiment.id, 10);

    const resultPayloads = itemPayloads.map((item) => ({
      ExperimentId: experimentId,
      LabSampleId: item.labSampleId || null,
      YourSampleId: item.yourSampleId || null,
      Analysis: item.analysis || null,
      AnalysisVariation: item.analysisVariation || null,
      TestMethod: item.testMethod || null,
      Unit: item.unit,
      SampleResult: item.sampleResult || null,
      TestValue: item.testValue,
      LOQ: item.loq || null,
      InSpec: item.inSpec || null,
      TestResults: item.testResults || null,
    }));

    const resultResponse = await fetch(`${API_URL}/experimentresultitems/batch`, {
      method: 'POST',
      headers: buildHeaders(true),
      body: JSON.stringify(resultPayloads),
    });

    if (!resultResponse.ok) {
      const text = await resultResponse.text();
      throw new Error(text || `Create experiment result items failed: HTTP ${resultResponse.status}`);
    }
  };

  const askOverwriteConfirm = useCallback((batchNumber: string, templateType: TemplateType) => {
    setOverwriteConfirmMessage(
      `The test result for batch ${batchNumber} and type ${templateType} already exists. Do you want to overwrite it?`
    );
    setOverwriteConfirmOpen(true);

    return new Promise<boolean>((resolve) => {
      overwriteConfirmResolverRef.current = resolve;
    });
  }, []);

  const resolveOverwriteConfirm = useCallback((confirmed: boolean) => {
    const resolver = overwriteConfirmResolverRef.current;
    overwriteConfirmResolverRef.current = null;
    setOverwriteConfirmOpen(false);
    setOverwriteConfirmMessage('');
    if (resolver) resolver(confirmed);
  }, []);

  const fetchExistingByBatchAndType = useCallback(async (batchNumber: string, templateType: TemplateType) => {
    const params = new URLSearchParams();
    params.set('range', '[0,999]');
    params.set('perPage', '1000');
    params.set('filter', JSON.stringify({ batchNumber }));

    const response = await fetch(`${API_URL}/experiments?${params.toString()}`, {
      method: 'GET',
      headers: buildHeaders(false),
    });

    if (!response.ok) {
      throw new Error(`Load existing experiment records failed: HTTP ${response.status}`);
    }

    const list = (await response.json()) as ExperimentRecord[];
    const normalizedBatch = String(batchNumber || '').trim();
    const normalizedType = String(templateType || '').trim().toUpperCase();

    return list.filter((item) => {
      const itemBatch = String(item.batchNumber || '').trim();
      const itemType = String(item.templateType || '').trim().toUpperCase();
      return itemBatch === normalizedBatch && itemType === normalizedType;
    });
  }, [API_URL]);

  const uploadExperimentWithOverwriteCheck = async (
    experimentPayload: {
      reference?: string;
      sampleDescription: string;
      batchNumber: string;
      description?: string;
      qcType?: string;
      sampleInformation?: string | null;
      timeRecord: string | null;
      exResult: number;
      templateType: TemplateType;
    },
    itemPayloads: Array<{
      labSampleId?: string;
      yourSampleId?: string;
      analysis?: string;
      analysisVariation?: string;
      testMethod?: string;
      unit: string;
      sampleResult?: string;
      testValue: string;
      loq?: string;
      inSpec?: string;
      testResults?: string;
    }>
  ): Promise<'created' | 'overwritten' | 'skipped'> => {
    const existing = await fetchExistingByBatchAndType(experimentPayload.batchNumber, experimentPayload.templateType);

    if (existing.length > 0) {
      const confirmed = await askOverwriteConfirm(experimentPayload.batchNumber, experimentPayload.templateType);
      if (!confirmed) {
        return 'skipped';
      }

      for (const record of existing) {
        const deleteResponse = await fetch(`${API_URL}/experiments/${encodeURIComponent(record.id)}`, {
          method: 'DELETE',
          headers: buildHeaders(false),
        });

        if (!deleteResponse.ok) {
          const text = await deleteResponse.text();
          throw new Error(text || `Delete existing experiment failed: HTTP ${deleteResponse.status}`);
        }
      }

      await uploadExperiment(experimentPayload, itemPayloads);
      return 'overwritten';
    }

    await uploadExperiment(experimentPayload, itemPayloads);
    return 'created';
  };

  const handleConfirmImport = async () => {
    const file = importFile;
    if (!file) return;

    setImporting(true);
    try {
      const upperName = file.name.toUpperCase();
      const extension = upperName.split('.').pop();
      if (!extension || !['XLS', 'XLSX', 'CSV'].includes(extension)) {
        throw new Error('Only .xlsx, .xls or .csv files are supported.');
      }

      const arrayBuffer = await file.arrayBuffer();
      const extensionLower = extension.toLowerCase();
      const workbook = extensionLower === 'csv'
        ? XLSX.read(decodeCsvText(arrayBuffer), { type: 'string', cellText: true, cellNF: true })
        : XLSX.read(arrayBuffer, { type: 'array', cellText: true, cellNF: true });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];

      if (!sheet) {
        throw new Error('No worksheet found in uploaded file.');
      }

      if (importTemplateType === 'AQ') {
        const rows = parseAQRows(sheet);
        const first = rows[0];

        const exResult = rows.every((row) => {
          const value = row.inSpec.toLowerCase();
          return value === 't' || value === 'true' || value === 'pass';
        })
          ? 1
          : 0;

        const result = await uploadExperimentWithOverwriteCheck(
          {
            reference: first.labSampleId,
            sampleDescription: first.sampleDescription,
            batchNumber: first.batchNumber,
            description: `Imported from AQ template: ${file.name}`,
            qcType: 'AQ',
            sampleInformation: null,
            timeRecord: first.timeRecord || null,
            exResult,
            templateType: 'AQ',
          },
          rows.map((row) => ({
            labSampleId: row.labSampleId,
            yourSampleId: row.yourSampleId || first.yourSampleId || first.batchNumber,
            analysis: row.analysis,
            analysisVariation: row.analysisVariation,
            testMethod: undefined,
            unit: row.unit || '-',
            sampleResult: row.sampleResult,
            testValue: row.testValue || row.sampleResult || '-',
            loq: undefined,
            inSpec: row.inSpec,
            testResults: undefined,
          }))
        );

        if (result === 'skipped') {
          notify('Import skipped: the existing batch/type record was kept.', { type: 'info' });
        } else {
          notify(`AQ template import succeeded: ${rows.length} result items imported`, { type: 'success' });
        }
      } else {
        const bnPayloads = parseBNRows(sheet);
        let createdCount = 0;
        let overwrittenCount = 0;
        let skippedCount = 0;

        for (const bn of bnPayloads) {
          const result = await uploadExperimentWithOverwriteCheck(
            {
              reference: bn.experiment.reference,
              sampleDescription: bn.experiment.sampleDescription,
              batchNumber: bn.experiment.batchNumber,
              description: bn.experiment.description,
              qcType: bn.experiment.qcType,
              sampleInformation: bn.experiment.sampleInformation,
              timeRecord: bn.experiment.timeRecord,
              exResult: 0,
              templateType: 'BN',
            },
            bn.items.map((item) => ({
              labSampleId: bn.experiment.labSampleId,
              yourSampleId: bn.experiment.yourSampleId,
              analysis: item.analysis,
              analysisVariation: undefined,
              testMethod: item.testMethod,
              unit: item.unit || '-',
              sampleResult: undefined,
              testValue: item.testValue,
              loq: item.loq,
              inSpec: undefined,
              testResults: undefined,
            }))
          );

          if (result === 'created') createdCount += 1;
          if (result === 'overwritten') overwrittenCount += 1;
          if (result === 'skipped') skippedCount += 1;
        }

        const totalItems = bnPayloads.reduce((sum, payload) => sum + payload.items.length, 0);
        if (createdCount + overwrittenCount > 0) {
          notify(
            `BN template import finished: ${createdCount} created, ${overwrittenCount} overwritten, ${skippedCount} skipped, ${totalItems} result items parsed`,
            { type: 'success' }
          );
        } else {
          notify('BN template import skipped: existing batch/type record(s) were kept.', { type: 'info' });
        }
      }

      await fetchExperiments(1, perPage, listFilter);
      setImportModalOpen(false);
      setImportFile(null);
      if (importFileInputRef.current) {
        importFileInputRef.current.value = '';
      }
    } catch (error) {
      notify(`Import failed: ${(error as Error).message}`, { type: 'error' });
    } finally {
      setImporting(false);
    }
  };

  const handleSelectImportFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setImportFile(file);
  };

  const handleViewDetail = async (experiment: ExperimentRecord) => {
    setDetailExperiment(experiment);
    setLoadingDetail(true);
    try {
      const params = new URLSearchParams();
      params.set('range', '[0,9999]');
      params.set('perPage', '10000');
      params.set('filter', JSON.stringify({ experimentId: experiment.id }));

      const response = await fetch(`${API_URL}/experimentresultitems?${params.toString()}`, {
        method: 'GET',
        headers: buildHeaders(false),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const items = (await response.json()) as ExperimentItem[];
      setDetailItems(items);
    } catch (error) {
      notify(`Load detail failed: ${(error as Error).message}`, { type: 'error' });
      setDetailItems([]);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleDeleteClick = (experimentId: string) => {
    setPendingDeleteId(experimentId);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) {
      return;
    }

    setDeletingExperimentId(pendingDeleteId);
    try {
      const response = await fetch(`${API_URL}/experiments/${pendingDeleteId}`, {
        method: 'DELETE',
        headers: buildHeaders(false),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `HTTP ${response.status}`);
      }

      const nextPage = experiments.length === 1 && page > 1 ? page - 1 : page;
      await fetchExperiments(nextPage, perPage, listFilter);
      notify('Experiment deleted successfully.', { type: 'success' });
      setDeleteConfirmOpen(false);
      setPendingDeleteId(null);
    } catch (error) {
      notify(`Delete failed: ${(error as Error).message}`, { type: 'error' });
    } finally {
      setDeletingExperimentId(null);
    }
  };

  const handleDetailUpdated = useCallback((updatedExperiment: ExperimentRecord, updatedItems: ExperimentItem[]) => {
    setDetailExperiment(updatedExperiment);
    setDetailItems(updatedItems);
  }, []);

  if (detailExperiment) {
    return (
      <ExperimentDataDetail
        experiment={detailExperiment}
        items={detailItems}
        loading={loadingDetail}
        onBack={() => setDetailExperiment(null)}
        onUpdated={handleDetailUpdated}
      />
    );
  }

  return (
    <div className="space-y-6" style={{ margin: 20 }}>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="mb-1 text-2xl">Experiment Data Management</h2>
          <p className="text-sm text-gray-500">Import templates and manage experiment data</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
            onClick={() => {
              setImportTemplateType('AQ');
              setImportFile(null);
              setImportModalOpen(true);
            }}
          >
            <Upload className="mr-2 size-4" />
            Import
          </Button>
        </div>
      </div>

      {importModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <h3 className="text-lg font-semibold text-slate-900">Import Template</h3>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  if (importing) return;
                  setImportModalOpen(false);
                  setImportFile(null);
                  if (importFileInputRef.current) {
                    importFileInputRef.current.value = '';
                  }
                }}
              >
                Close
              </Button>
            </div>

            <div className="space-y-4 px-5 py-4">
              <div className="space-y-2">
                <Label className="text-sm text-slate-700">1. Select Type</Label>
                <select
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  value={importTemplateType}
                  onChange={(e) => setImportTemplateType(e.target.value as TemplateType)}
                  disabled={importing}
                >
                  <option value="AQ">AQ</option>
                  <option value="BN">CAIQ</option>
                </select>
                <p className="text-xs text-slate-500">Choose the template type to import and parse.</p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm text-slate-700">2. Choose File</Label>
                <input
                  ref={importFileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="sr-only"
                  onChange={handleSelectImportFile}
                  disabled={importing}
                />
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => importFileInputRef.current?.click()}
                    disabled={importing}
                  >
                    Choose File
                  </Button>
                  <span className="text-sm text-slate-600">
                    {importFile ? importFile.name : 'No file selected'}
                  </span>
                </div>
                <p className="text-xs text-slate-500">
                  {importFile ? `Selected: ${importFile.name}` : 'No file selected'}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  if (importing) return;
                  setImportModalOpen(false);
                  setImportFile(null);
                  if (importFileInputRef.current) {
                    importFileInputRef.current.value = '';
                  }
                }}
                disabled={importing}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="bg-blue-600 text-white hover:bg-blue-700"
                onClick={handleConfirmImport}
                disabled={importing || !importFile}
              >
                {importing ? 'Importing...' : 'Import and Parse'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 bg-slate-50 px-4 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <Label htmlFor="experiment-batch-number" className="text-sm text-gray-700">Batch Number</Label>
            <Input
              id="experiment-batch-number"
              value={batchNumberQuery}
              onChange={(e) => setBatchNumberQuery(e.target.value)}
              placeholder="Search batch number"
              className="w-full max-w-xs bg-white"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full table-fixed">
            <thead>
              <tr className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                <th className="w-[15%] px-4 py-3 text-left text-xs uppercase tracking-wider text-gray-600">Batch Number</th>
                <th className="w-[20%] px-4 py-3 text-left text-xs uppercase tracking-wider text-gray-600">Description</th>
                <th className="w-[10%] px-4 py-3 text-left text-xs uppercase tracking-wider text-gray-600">Type</th>
                <th className="w-[10%] px-4 py-3 text-left text-xs uppercase tracking-wider text-gray-600">CreatedBy</th>
                <th className="w-[15%] px-4 py-3 text-left text-xs uppercase tracking-wider text-gray-600">CreatedAt</th>
                <th className="w-[15%] px-4 py-3 text-left text-xs uppercase tracking-wider text-gray-600">UpdatedAt</th>
                <th className="w-[15%] px-4 py-3 text-center text-xs uppercase tracking-wider text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {experiments.map((experiment) => (
                <tr key={experiment.id} className="transition-colors hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900"><span className="block truncate" title={experiment.batchNumber}>{experiment.batchNumber}</span></td>
                  <td className="px-4 py-3 text-sm text-gray-900"><span className="block truncate" title={experiment.sampleDescription || '-'}>{experiment.sampleDescription || '-'}</span></td>
                  <td className="px-4 py-3 text-sm text-gray-700">{experiment.templateType || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{userNameMap[String(experiment.createBy)] || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{new Date(experiment.createdAt).toLocaleString('zh-CN', { hour12: false })}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{new Date(experiment.updatedAt).toLocaleString('zh-CN', { hour12: false })}</td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewDetail(experiment)}
                      className="text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700"
                      disabled={deletingExperimentId === experiment.id}
                      title="Details"
                    >
                      <Eye className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteClick(experiment.id)}
                      className="ml-1 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                      disabled={deletingExperimentId === experiment.id}
                      title="Delete"
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {!loadingList && experiments.length === 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center shadow-sm">
          <h3 className="mb-2 text-lg text-gray-600">No experiment records</h3>
          <p className="text-sm text-gray-400">Click Import, select AQ or BN in the popup, then upload and parse a file.</p>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 text-sm text-gray-600">
        <Button
          variant="outline"
          onClick={() => setPage((prev) => Math.max(1, prev - 1))}
          disabled={page <= 1 || loadingList}
        >
          Prev
        </Button>
        <span>
          Page {page} / {totalPages}
        </span>
        <Button
          variant="outline"
          onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
          disabled={page >= totalPages || loadingList}
        >
          Next
        </Button>
      </div>

      <Confirm
        isOpen={deleteConfirmOpen}
        loading={deletingExperimentId !== null}
        title="Confirm Delete"
        content="Are you sure you want to delete this experiment record?"
        onConfirm={handleConfirmDelete}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setPendingDeleteId(null);
        }}
      />

      <Confirm
        isOpen={overwriteConfirmOpen}
        loading={false}
        title="Overwrite Existing Result"
        content={overwriteConfirmMessage}
        onConfirm={() => resolveOverwriteConfirm(true)}
        onClose={() => resolveOverwriteConfirm(false)}
      />
    </div>
  );
}
